import React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import ListTableContainer from '../../component/ListTableContainer'
import { formatRate, getDailyMetricAverages } from './onboardingFunnelUtils'

export const ENTRY_AVERAGE_METRICS = [
    { key: 'firstOpenUsers', label: '최초 실행' },
    { key: 'signupStartedUsers', label: '당일 가입 시작' },
    { key: 'notStartedUsers', label: '미진입' },
    { key: 'signupStartRate', label: '당일 가입 시작률', rate: true },
]

export const COHORT_AVERAGE_METRICS = [
    { key: 'users', label: '일반회원 선택 완료' },
    { key: 'analyticsCompletedUsers', label: 'Analytics 가입 완료' },
    { key: 'sameDayCompletionRate', label: '당일 가입 완료율', rate: true },
]

export const STEP_AVERAGE_METRICS = [
    { key: 'entryUsers', label: '진입 사용자' },
    { key: 'completeUsers', label: '완료 사용자' },
    { key: 'completionRate', label: '단계 완료율', rate: true },
    { key: 'internalDropRate', label: '내부 이탈률', rate: true },
    { key: 'transitionDropRate', label: '전환 이탈률', rate: true },
    { key: 'cumulativeRate', label: '누적 도달률', rate: true },
]

export default function OnboardingPeriodAverage({
    label, rangeLabel, groups, metrics, groupColumnLabel = '구분', showPlatformBreakdown = false,
}) {
    return (
        <Box component="section" aria-label={label} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                조회기간 일평균
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {rangeLabel} · 지표별 유효한 일별 수치의 산술평균입니다. 비율도 일별 비율의 평균이며,
                누락값·계산 불가 값은 제외하고 0은 포함합니다. 잠정치가 포함될 수 있습니다.
                {showPlatformBreakdown && ' 전체 (iOS / Android) 순서이며, 데이터가 없는 OS는 -로 표시합니다.'}
            </Typography>
            <ListTableContainer>
                <Table size="small" aria-label={`${label} 표`} sx={{ minWidth: showPlatformBreakdown ? 1000 : 180 + metrics.length * 145 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{groupColumnLabel}</TableCell>
                            {metrics.map(metric => (
                                <TableCell key={metric.key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    평균 {metric.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map((group) => {
                            const averages = getDailyMetricAverages(group.rows, metrics.map(metric => metric.key))
                            return (
                                <TableRow key={group.key} hover>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {group.label}
                                    </TableCell>
                                    {metrics.map((metric) => {
                                        const average = averages[metric.key]
                                        const value = average.value === null
                                            ? '-'
                                            : metric.rate
                                                ? formatRate(average.value)
                                                : `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(average.value)}명`
                                        const platformValues = showPlatformBreakdown
                                            ? ['IOS', 'ANDROID'].map(os => {
                                                const platformAverage = getDailyMetricAverages(
                                                    group.rows.map(row => row.byPlatform?.[os]), [metric.key]
                                                )[metric.key]
                                                return platformAverage.value === null ? '-'
                                                    : metric.rate ? formatRate(platformAverage.value)
                                                        : `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(platformAverage.value)}명`
                                            })
                                            : null
                                        return (
                                            <TableCell
                                                key={metric.key}
                                                align="right"
                                                aria-label={`${group.label} 평균 ${metric.label}`}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {value}
                                                    {platformValues && (
                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
                                                            ({platformValues.join(' / ')})
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">{average.days}일 집계</Typography>
                                            </TableCell>
                                        )
                                    })}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </ListTableContainer>
        </Box>
    )
}
